/**
 * BOSS İzinler sayfası — tüm talepler tıklanabilir, durum değiştirilebilir.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Calendar, Clock, Check, XCircle, MessageSquare, RefreshCw,
  AlertCircle, Filter, X,
} from 'lucide-react'
import {
  izinleriGetir,
  DURUM_ETIKETLERI, KAYNAK_ETIKETLERI,
  type IzinTalebi, type IzinDurumu,
} from '@/lib/services/izin-service'
import { tumPersonelleriGetir } from '@/lib/services/personel-service'
import { IzinOnayModal } from './IzinOnayModal'
import type { KullaniciPersonel } from '@/types'

function tarihEtiketi(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })
}

function durumRengi(d: IzinDurumu): string {
  switch (d) {
    case 'onaylandi': return 'bg-green-100 text-green-800 border-green-200'
    case 'reddedildi': return 'bg-red-100 text-red-800 border-red-200'
    case 'iptal': return 'bg-gray-100 text-gray-700 border-gray-200'
    default: return 'bg-yellow-100 text-yellow-800 border-yellow-200'
  }
}

function DurumIkonu({ durum }: { durum: IzinDurumu }) {
  switch (durum) {
    case 'onaylandi': return <Check className="w-3.5 h-3.5" />
    case 'reddedildi': return <XCircle className="w-3.5 h-3.5" />
    case 'iptal': return <X className="w-3.5 h-3.5" />
    default: return <Clock className="w-3.5 h-3.5" />
  }
}

type FiltreT = 'hepsi' | IzinDurumu

export function IzinlerPage() {
  const [talepler, setTalepler] = useState<IzinTalebi[]>([])
  const [personeller, setPersoneller] = useState<Map<string, KullaniciPersonel>>(new Map())
  const [yukleniyor, setYukleniyor] = useState(true)
  const [hata, setHata] = useState<string | null>(null)
  const [filtre, setFiltre] = useState<FiltreT>('hepsi')
  const [secilen, setSecilen] = useState<IzinTalebi | null>(null)

  const yukle = useCallback(async () => {
    setYukleniyor(true)
    try {
      const [tumTalepler, tumPersoneller] = await Promise.all([
        izinleriGetir({}),
        tumPersonelleriGetir(),
      ])
      setTalepler(tumTalepler)
      const m = new Map<string, KullaniciPersonel>()
      tumPersoneller.forEach((p) => m.set(p.id, p))
      setPersoneller(m)
      setHata(null)
    } catch (err) {
      console.error(err)
      setHata('Talepler yüklenemedi')
    } finally {
      setYukleniyor(false)
    }
  }, [])

  useEffect(() => { yukle() }, [yukle])

  const bekleyen = useMemo(
    () => talepler.filter((t) => t.durum === 'bekliyor'),
    [talepler]
  )

  const filtrelenmis = useMemo(() => {
    if (filtre === 'hepsi') return talepler
    return talepler.filter((t) => t.durum === filtre)
  }, [talepler, filtre])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">İzin Talepleri</h1>
          <p className="text-sm text-gray-600 mt-1">
            Personel izin talepleri — onaylanmış veya reddedilmiş talepler de düzenlenebilir
          </p>
        </div>
        <button
          onClick={yukle}
          disabled={yukleniyor}
          className="text-brand-600 hover:underline disabled:opacity-50 inline-flex items-center gap-1 text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${yukleniyor ? 'animate-spin' : ''}`} />
          Yenile
        </button>
      </div>

      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
          {hata}
        </div>
      )}

      {bekleyen.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <h2 className="text-lg font-bold text-gray-900">
              Onay Bekleyenler
              <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-sm rounded-full">
                {bekleyen.length}
              </span>
            </h2>
          </div>
          <div className="space-y-2">
            {bekleyen.map((t) => (
              <TalepKarti
                key={t.id}
                talep={t}
                personel={personeller.get(t.personelId)}
                vurgulu
                onTikla={() => setSecilen(t)}
              />
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-900">Tüm Talepler</h2>
          <div className="flex items-center gap-1.5">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={filtre}
              onChange={(e) => setFiltre(e.target.value as FiltreT)}
              className="text-sm border border-gray-300 rounded-lg px-2 py-1"
            >
              <option value="hepsi">Hepsi ({talepler.length})</option>
              <option value="bekliyor">Bekleyen ({bekleyen.length})</option>
              <option value="onaylandi">
                Onaylı ({talepler.filter(t => t.durum === 'onaylandi').length})
              </option>
              <option value="reddedildi">
                Reddedilen ({talepler.filter(t => t.durum === 'reddedildi').length})
              </option>
              <option value="iptal">
                İptal ({talepler.filter(t => t.durum === 'iptal').length})
              </option>
            </select>
          </div>
        </div>

        {yukleniyor && talepler.length === 0 ? (
          <div className="card text-center py-8">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600"></div>
          </div>
        ) : filtrelenmis.length === 0 ? (
          <div className="card text-center py-8">
            <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-700">
              {filtre === 'hepsi' ? 'Henüz izin talebi yok' : 'Bu filtrede sonuç yok'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtrelenmis.map((t) => (
              <TalepKarti
                key={t.id}
                talep={t}
                personel={personeller.get(t.personelId)}
                onTikla={() => setSecilen(t)}
              />
            ))}
          </div>
        )}
      </div>

      <IzinOnayModal
        acik={!!secilen}
        talep={secilen}
        personel={secilen ? personeller.get(secilen.personelId) : undefined}
        onKapat={() => setSecilen(null)}
        onIslem={() => {
          setSecilen(null)
          yukle()
        }}
      />
    </div>
  )
}

interface TalepKartiProps {
  talep: IzinTalebi
  personel?: KullaniciPersonel
  vurgulu?: boolean
  onTikla?: () => void
}

function TalepKarti({ talep, personel, vurgulu, onTikla }: TalepKartiProps) {
  const tiklanabilir = !!onTikla
  return (
    <div
      onClick={onTikla}
      className={`card transition-all ${
        vurgulu ? 'border-yellow-300 bg-yellow-50/30 ring-1 ring-yellow-200' : ''
      } ${tiklanabilir ? 'cursor-pointer hover:shadow-md hover:border-brand-300' : ''}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-bold text-gray-900 truncate">
              {personel?.isim ?? 'Bilinmeyen'}
            </h3>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${durumRengi(talep.durum)}`}>
              <DurumIkonu durum={talep.durum} />
              {DURUM_ETIKETLERI[talep.durum]}
            </span>
            {talep.sorumluAtamasi && (
              <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full font-semibold">
                Sorumlu Atadı
              </span>
            )}
          </div>

          <p className="text-sm text-gray-700">
            {tarihEtiketi(talep.baslangicTarihi)}
            {talep.gunSayisi > 1 && (
              <span> – {tarihEtiketi(talep.bitisTarihi)}</span>
            )}
            <span className="text-gray-500"> · {talep.gunSayisi} gün</span>
          </p>

          <p className="text-xs text-gray-500 mt-0.5">
            {talep.kaynak && (
              <>
                Kaynak: <span className="text-purple-700 font-medium">
                  {KAYNAK_ETIKETLERI[talep.kaynak]}
                </span>
                {' · '}
              </>
            )}
            Talep: {talep.talepTarihi.toDate().toLocaleDateString('tr-TR', {
              day: 'numeric', month: 'short',
            })}
          </p>

          {talep.aciklama && (
            <div className="mt-2 bg-gray-50 rounded-lg p-2 text-xs text-gray-700 flex items-start gap-1.5">
              <MessageSquare className="w-3 h-3 flex-shrink-0 mt-0.5 text-gray-500" />
              <span>{talep.aciklama}</span>
            </div>
          )}

          {talep.durum === 'reddedildi' && talep.redSebebi && (
            <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-800">
              <strong>Red sebebi:</strong> {talep.redSebebi}
            </div>
          )}
        </div>

        {tiklanabilir && (
          <span className="text-xs text-brand-600 font-semibold flex-shrink-0">
            {talep.durum === 'bekliyor' ? 'İncele →' : 'Düzenle →'}
          </span>
        )}
      </div>
    </div>
  )
}
